/**
 * useCortexEngine — shared streaming engine for the unified agent window.
 *
 * One brain connection (`createBrainSynapticConnection` → TanStack AI) feeding
 * a normalized `CortexTimeline` via `cortexStreamReducer`, merged with the
 * durable `cortex_run_events` journal (useRunTimeline). Message persistence is
 * delegated to a `SessionStore` adapter so the same engine serves both
 * `chat` (chat_sessions) and `work` (cortex_sessions) session kinds.
 */

import { useAuth } from "@/hooks/useAuth";
import { useCurrentOrganization } from "@/hooks/useCurrentOrganization";
import { useCurrentProject } from "@/hooks/useCurrentProject";
import { useAISettings } from "@/hooks/useAISettings";
import { useGoogleGeminiConnection } from "@/hooks/useGoogleGeminiConnection";
import { supabase } from "@/integrations/supabase/client";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  useTanstackChat,
  createBrainSynapticConnection,
} from "@flowstate/ai/react";
import {
  useCortexRunAwait,
  isGeminiByokFallbackChunk,
  GEMINI_BYOK_FALLBACK_COPY,
  resolveCortexBrainUrl,
} from "@flowstate/ai-client";
import type {
  BrainAwaitingResponse,
  CortexRunRow,
  MessageAttachment,
  ReasoningEffort,
} from "@flowstate/types";
import type { UIMessage } from "@flowstate/ai/react";
import { toast } from "@/components/ui/sonner";
import {
  DEFAULT_AI_SETTINGS,
  DEFAULT_GEMINI_FLASH_MODEL,
  resolveGeminiModelId,
} from "@flowstate/types";
import { logger } from "@/lib/logger";
import { geminiAuthService } from "@/services/integrations/core/gemini-auth.service";
import {
  createTimeline,
  reduceBrainChunk,
  type CortexTimeline,
  type TimelineRun,
} from "./cortexStreamReducer";
import { createSessionStore, type AgentSessionSummary, type SessionKind, type SessionStore } from "./sessionStores";
import { useRunTimeline } from "./useRunTimeline";

export interface CortexEngineOptions {
  /** Initial session kind — the rail can flip this via selectSession. */
  kind?: SessionKind;
  /** Called when the active session changes (for URL sync etc.). */
  onSessionChange?: (sessionId: string | undefined) => void;
}

export function useCortexEngine({ kind: initialKind = "chat", onSessionChange }: CortexEngineOptions) {
  const { user, isAuthenticated } = useAuth();
  const { currentOrganization } = useCurrentOrganization();
  const currentOrgId = currentOrganization?.id;
  const { currentProject } = useCurrentProject();
  const { settings: userAISettings } = useAISettings();
  const { accessToken: geminiOAuthToken, isConnected: isGeminiOAuthConnected } =
    useGoogleGeminiConnection();

  const [activeKind, setActiveKind] = useState<SessionKind>(initialKind);
  const [sessionId, setSessionIdState] = useState<string | undefined>(undefined);
  const [sessions, setSessions] = useState<AgentSessionSummary[]>([]);
  // Both kinds listed for the unified session rail.
  const [sessionLists, setSessionLists] = useState<{
    chats: AgentSessionSummary[];
    work: AgentSessionSummary[];
  }>({ chats: [], work: [] });
  // A session load requested before its kind's store is live (kind flip).
  const pendingLoadRef = useRef<string | null>(null);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [liveTimeline, setLiveTimeline] = useState<CortexTimeline>(() => createTimeline());
  const [pausedRun, setPausedRun] = useState<{
    runId: string;
    status: "awaiting" | "awaiting_input";
    question?: string;
    options?: string[];
    allowFreeText?: boolean;
    message?: string;
    specialistType?: string;
  } | null>(null);

  const messagesRef = useRef<UIMessage[]>([]);
  const sessionIdRef = useRef(sessionId);
  const activeCortexRunRef = useRef<string | null>(null);
  const byokNoticeShownRef = useRef(false);
  const lastBrainErrorRef = useRef<string | null>(null);
  const intentionalStopRef = useRef(false);
  const messageQueueRef = useRef<string[]>([]);
  const [queuedCount, setQueuedCount] = useState(0);

  // Refs for dynamic values read inside the stable connection callback.
  const userRef = useRef(user);
  const orgRef = useRef(currentOrgId);
  const projectRef = useRef(currentProject);
  const aiSettingsRef = useRef(userAISettings);
  const geminiTokenRef = useRef(geminiOAuthToken);
  const geminiConnectedRef = useRef(isGeminiOAuthConnected);

  useEffect(() => { sessionIdRef.current = sessionId; }, [sessionId]);
  useEffect(() => { userRef.current = user; }, [user]);
  useEffect(() => { orgRef.current = currentOrgId; }, [currentOrgId]);
  useEffect(() => { projectRef.current = currentProject; }, [currentProject]);
  useEffect(() => { aiSettingsRef.current = userAISettings; }, [userAISettings]);
  useEffect(() => { geminiTokenRef.current = geminiOAuthToken; }, [geminiOAuthToken]);
  useEffect(() => { geminiConnectedRef.current = isGeminiOAuthConnected; }, [isGeminiOAuthConnected]);

  const getToken = useCallback(async () => {
    const { data } = await supabase.auth.getSession();
    return data.session?.access_token;
  }, []);

  const store: SessionStore = useMemo(
    () => createSessionStore(activeKind, getToken),
    [activeKind, getToken],
  );
  const kind = activeKind;

  // Model/provider selection (same localStorage keys as the drawer for parity)
  const [selectedModel, setSelectedModel] = useState<string>(() => {
    try {
      return localStorage.getItem("flowstate.chat.selectedModel") ?? DEFAULT_GEMINI_FLASH_MODEL;
    } catch {
      return DEFAULT_GEMINI_FLASH_MODEL;
    }
  });
  const [selectedProvider, setSelectedProvider] = useState<"gemini" | "openrouter">(() => {
    try {
      const stored =
        localStorage.getItem("flowstate.chat.selectedProvider") ??
        DEFAULT_AI_SETTINGS.provider ??
        "gemini";
      return stored === "openrouter" ? "openrouter" : "gemini";
    } catch {
      return "gemini";
    }
  });
  const [reasoningEffort, setReasoningEffort] = useState<ReasoningEffort>(() => {
    try {
      const s = localStorage.getItem("flowstate.chat.reasoningEffort");
      return s === "low" || s === "medium" || s === "high" ? s : "medium";
    } catch {
      return "medium";
    }
  });
  const [selectedSpecialist, setSelectedSpecialist] = useState<string | undefined>();
  const [selectedNotebookId, setSelectedNotebookId] = useState<string | undefined>();

  const modelRef = useRef(selectedModel);
  const providerRef = useRef(selectedProvider);
  const effortRef = useRef(reasoningEffort);
  const specialistRef = useRef(selectedSpecialist);
  const notebookRef = useRef(selectedNotebookId);
  useEffect(() => { modelRef.current = selectedModel; }, [selectedModel]);
  useEffect(() => { providerRef.current = selectedProvider; }, [selectedProvider]);
  useEffect(() => { effortRef.current = reasoningEffort; }, [reasoningEffort]);
  useEffect(() => { specialistRef.current = selectedSpecialist; }, [selectedSpecialist]);
  useEffect(() => { notebookRef.current = selectedNotebookId; }, [selectedNotebookId]);

  // Pending file attachments — mirrored into a ref so the request-body
  // builder (called at fetch time, not render time) sees them.
  const [pendingAttachments, setPendingAttachments] = useState<MessageAttachment[]>([]);
  const attachmentsRef = useRef<MessageAttachment[]>([]);
  const handleFileUploaded = useCallback((attachment: MessageAttachment) => {
    setPendingAttachments((prev) => {
      const next = [...prev, attachment];
      attachmentsRef.current = next;
      return next;
    });
  }, []);
  useEffect(() => {
    try { localStorage.setItem("flowstate.chat.selectedModel", selectedModel); } catch { /* quota */ }
  }, [selectedModel]);
  useEffect(() => {
    try { localStorage.setItem("flowstate.chat.selectedProvider", selectedProvider); } catch { /* quota */ }
  }, [selectedProvider]);
  useEffect(() => {
    try { localStorage.setItem("flowstate.chat.reasoningEffort", reasoningEffort); } catch { /* quota */ }
  }, [reasoningEffort]);

  const BRAIN_URL = resolveCortexBrainUrl();

  const handleAwaiting = useCallback((res: BrainAwaitingResponse) => {
    if (res.status === "awaiting_input") {
      setPausedRun({
        runId: res.runId,
        status: "awaiting_input",
        question: res.question,
        options: res.options,
        allowFreeText: res.allowFreeText,
      });
      // Fold the question into the live timeline too.
      setLiveTimeline((tl) =>
        reduceBrainChunk(tl, {
          type: "CUSTOM",
          name: "user-question",
          data: {
            question: res.question,
            options: res.options,
            allowFreeText: res.allowFreeText,
          },
          cortexRunId: res.runId,
        }),
      );
      return;
    }
    setPausedRun({
      runId: res.runId,
      status: "awaiting",
      message: res.message,
      specialistType: res.specialistType,
    });
    toast.message("Working in the background", {
      description: res.message ?? "You will be notified when it finishes.",
    });
  }, []);

  const connection = useMemo(
    () =>
      createBrainSynapticConnection(BRAIN_URL, async () => {
        const token = await getToken();
        const userId = userRef.current?.id;
        if (userId && geminiConnectedRef.current && aiSettingsRef.current.useGoogleGemini !== false) {
          const tokenResult = await geminiAuthService.getValidToken(userId);
          if (tokenResult.success && tokenResult.data?.access_token) {
            geminiTokenRef.current = tokenResult.data.access_token;
          }
        }
        // Ensure the session row exists before the run is leased.
        if (userId && !sessionIdRef.current) {
          const created = await store.ensure(userId, {
            orgId: orgRef.current,
            projectId: projectRef.current?.id,
          });
          if (created) {
            sessionIdRef.current = created;
            setSessionIdState(created);
            onSessionChange?.(created);
          }
        }
        return {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: {
            userId,
            sessionId: sessionIdRef.current,
            settings: {
              ...aiSettingsRef.current,
              model: modelRef.current,
              primaryProvider: providerRef.current,
              primaryModel:
                providerRef.current === "openrouter"
                  ? modelRef.current
                  : resolveGeminiModelId(modelRef.current),
              reasoningEffort: effortRef.current,
              geminiApiKey: undefined,
              openrouterApiKey: undefined,
              ...(geminiConnectedRef.current &&
              aiSettingsRef.current.useGoogleGemini !== false &&
              geminiTokenRef.current
                ? { geminiAccessToken: geminiTokenRef.current }
                : {}),
            },
            agentContext: {
              organizationId: orgRef.current,
              organizationName: currentOrganization?.name,
              selectedProjectId: projectRef.current?.id,
            },
            ...(specialistRef.current ? { specialist: specialistRef.current } : {}),
            ...(notebookRef.current ? { notebookId: notebookRef.current } : {}),
            ...(attachmentsRef.current.length > 0
              ? { attachments: attachmentsRef.current }
              : {}),
          },
        };
      }, handleAwaiting),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refs keep dynamic values fresh
    [BRAIN_URL, getToken, store, handleAwaiting],
  );

  const tanstackChat = useTanstackChat({
    connection,
    onChunk: (chunk) => {
      if (chunk.type === "RUN_STARTED") {
        activeCortexRunRef.current =
          typeof chunk.cortexRunId === "string" ? chunk.cortexRunId : null;
        intentionalStopRef.current = false;
      }
      if (chunk.type === "RUN_FINISHED") {
        activeCortexRunRef.current = null;
      }
      if (chunk.type === "RUN_ERROR") {
        const msg = chunk.error?.message ?? "Chat request failed";
        lastBrainErrorRef.current = msg;
        logger.error("Brain RUN_ERROR", { message: msg });
        toast.error(msg);
      }
      if (isGeminiByokFallbackChunk(chunk) && !byokNoticeShownRef.current) {
        byokNoticeShownRef.current = true;
        toast.info(GEMINI_BYOK_FALLBACK_COPY.title, {
          description: GEMINI_BYOK_FALLBACK_COPY.description,
        });
      }
      setLiveTimeline((tl) => reduceBrainChunk(tl, chunk));
    },
    onFinish: (message) => {
      // Persist assistant message via the store.
      const text = message.parts
        .filter((p) => p.type === "text")
        .map((p) => ("content" in p ? (p as { content: string }).content : ""))
        .join("");
      const sid = sessionIdRef.current;
      const uid = userRef.current?.id;
      if (sid && uid && text.trim()) {
        void store
          .saveMessage(sid, { role: "assistant", content: text }, { userId: uid, orgId: orgRef.current })
          .catch((err) => logger.warn("[engine] assistant save failed", { error: err }));
      }
      // Flush queued message (Devin-style follow-up while streaming).
      const nextQueued = messageQueueRef.current.shift();
      setQueuedCount(messageQueueRef.current.length);
      if (nextQueued) {
        void sendInternal(nextQueued);
      }
    },
    onError: (error) => {
      lastBrainErrorRef.current = error.message;
      const droppedRunId = activeCortexRunRef.current;
      activeCortexRunRef.current = null;
      if (droppedRunId && !intentionalStopRef.current) {
        // Transport dropped mid-run — watch the run row for completion.
        setPausedRun({ runId: droppedRunId, status: "awaiting" });
        toast.message("Connection lost — recovering run", {
          description: "The task is still running; the result will appear when it finishes.",
        });
        return;
      }
      toast.error(error.message);
    },
  });

  useEffect(() => { messagesRef.current = tanstackChat.messages; }, [tanstackChat.messages]);

  // ── Durable timeline merge ──────────────────────────────────────────────
  const persisted = useRunTimeline(sessionId);
  const timeline: CortexTimeline = useMemo(() => {
    const merged = createTimeline();
    merged.order = [...persisted.timeline.order];
    merged.byId = { ...persisted.timeline.byId };
    for (const id of liveTimeline.order) {
      const live = liveTimeline.byId[id];
      // Live run wins when it exists in both (it has fresher events).
      merged.byId[id] = live;
      if (!merged.order.includes(id)) merged.order.push(id);
    }
    return merged;
  }, [persisted.timeline, liveTimeline]);

  const runs: TimelineRun[] = useMemo(
    () => timeline.order.map((id) => timeline.byId[id]),
    [timeline],
  );

  // ── Run watcher for paused/awaiting runs ────────────────────────────────
  useCortexRunAwait(supabase, sessionId, pausedRun?.status === "awaiting" ? pausedRun.runId : undefined, {
    enabled: Boolean(sessionId && pausedRun?.status === "awaiting"),
    onComplete: (run: CortexRunRow) => {
      const text = run.response_output ?? "Task completed.";
      const msgId = crypto.randomUUID();
      tanstackChat.setMessages([
        ...messagesRef.current,
        { id: msgId, role: "assistant", parts: [{ type: "text", content: text }] },
      ]);
      const uid = userRef.current?.id;
      const sid = sessionIdRef.current;
      if (sid && uid) {
        void store.saveMessage(sid, { role: "assistant", content: text }, { userId: uid, orgId: orgRef.current })
          .catch((err) => logger.warn("[engine] recovered save failed", { error: err }));
      }
      setPausedRun(null);
      toast.success("Background task complete");
    },
    onFailed: (run) => {
      setPausedRun(null);
      toast.error(run.error_message ?? "Background task failed");
    },
  });

  // ── Session management ──────────────────────────────────────────────────
  const refreshSessions = useCallback(async () => {
    const uid = userRef.current?.id;
    if (!uid) return;
    const list = await store.list(uid, orgRef.current);
    setSessions(list);
    // Refresh both kinds for the rail — chat store is cheap, work store needs a token.
    const chatStore = createSessionStore("chat", getToken);
    const workStore = createSessionStore("work", getToken);
    const [chats, work] = await Promise.all([
      chatStore.list(uid, orgRef.current),
      workStore.list(uid, orgRef.current),
    ]);
    setSessionLists({ chats, work });
  }, [store, getToken]);

  useEffect(() => {
    if (isAuthenticated) void refreshSessions();
  }, [isAuthenticated, refreshSessions]);

  const loadSession = useCallback(
    async (id: string) => {
      setIsLoadingMessages(true);
      try {
        const msgs = await store.loadMessages(id);
        tanstackChat.setMessages(msgs);
        setSessionIdState(id);
        sessionIdRef.current = id;
        setLiveTimeline(createTimeline());
        setPausedRun(null);
        onSessionChange?.(id);
      } catch (err) {
        logger.warn("[engine] loadSession failed", { error: err });
        toast.error("Failed to load session");
      } finally {
        setIsLoadingMessages(false);
      }
    },
    [store, tanstackChat, onSessionChange],
  );

  /**
   * Unified rail entry point — flips the session kind (store) when needed,
   * then loads. The load is deferred via pendingLoadRef so the store memo
   * for the new kind is in place before loadSession runs.
   */
  const selectSession = useCallback(
    (k: SessionKind, id: string) => {
      if (k !== activeKind) {
        pendingLoadRef.current = id;
        setActiveKind(k);
        return;
      }
      void loadSession(id);
    },
    [activeKind, loadSession],
  );

  // Complete a deferred load once the new kind's store is live.
  useEffect(() => {
    const pending = pendingLoadRef.current;
    if (pending) {
      pendingLoadRef.current = null;
      void loadSession(pending);
    }
  }, [store, loadSession]);

  const startNewSession = useCallback((k?: SessionKind) => {
    if (k && k !== activeKind) setActiveKind(k);
    setSessionIdState(undefined);
    sessionIdRef.current = undefined;
    tanstackChat.setMessages([]);
    setLiveTimeline(createTimeline());
    setPausedRun(null);
    onSessionChange?.(undefined);
  }, [tanstackChat, onSessionChange, activeKind]);

  const deleteSession = useCallback(
    async (id: string) => {
      await store.remove(id);
      if (sessionIdRef.current === id) startNewSession();
      await refreshSessions();
    },
    [store, refreshSessions, startNewSession],
  );

  const renameSession = useCallback(
    async (id: string, title: string) => {
      await store.rename(id, title);
      await refreshSessions();
    },
    [store, refreshSessions],
  );

  // ── Send / stop / queue ─────────────────────────────────────────────────
  const sendInternal = useCallback(
    async (text: string) => {
      const uid = userRef.current?.id;
      let sid = sessionIdRef.current;
      if (uid && !sid) {
        sid = await store.ensure(uid, {
          titleHint: text,
          orgId: orgRef.current,
          projectId: projectRef.current?.id,
        });
        if (sid) {
          sessionIdRef.current = sid;
          setSessionIdState(sid);
          onSessionChange?.(sid);
        }
      }
      // Persist the user message.
      if (sid && uid) {
        void store
          .saveMessage(sid, { role: "user", content: text }, { userId: uid, orgId: orgRef.current })
          .catch((err) => logger.warn("[engine] user save failed", { error: err }));
      }
      byokNoticeShownRef.current = false;
      lastBrainErrorRef.current = null;
      await tanstackChat.sendMessage(text);
      setPendingAttachments([]);
      attachmentsRef.current = [];
      void refreshSessions();
    },
    [store, tanstackChat, refreshSessions, onSessionChange],
  );

  const send = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      // Queue follow-ups while a response is generating (Devin-style).
      if (tanstackChat.isLoading || pausedRun?.status === "awaiting") {
        messageQueueRef.current.push(trimmed);
        setQueuedCount(messageQueueRef.current.length);
        return;
      }
      await sendInternal(trimmed);
    },
    [tanstackChat.isLoading, pausedRun, sendInternal],
  );

  const stop = useCallback(() => {
    intentionalStopRef.current = true;
    activeCortexRunRef.current = null;
    messageQueueRef.current = [];
    setQueuedCount(0);
    tanstackChat.stop();
  }, [tanstackChat]);

  /**
   * Promote the active chat session to a durable work session — copies the
   * message history into cortex_sessions and switches the engine's kind.
   */
  const promoteToWorkSession = useCallback(async () => {
    const uid = userRef.current?.id;
    const sid = sessionIdRef.current;
    if (!uid || !sid || activeKind !== "chat") return;

    try {
      const workStore = createSessionStore("work", getToken);
      const chatStore = createSessionStore("chat", getToken);
      const msgs = await chatStore.loadMessages(sid);
      const title =
        sessions.find((s) => s.id === sid)?.title ??
        msgs.find((m) => m.role === "user")?.parts
          .map((p) => (p.type === "text" && "content" in p ? String(p.content) : ""))
          .join("")
          .slice(0, 60) ??
        "Work session";

      const newId = await workStore.ensure(uid, {
        titleHint: title,
        orgId: orgRef.current,
        projectId: projectRef.current?.id,
      });
      if (!newId) throw new Error("Failed to create work session");

      for (const m of msgs) {
        const text = m.parts
          .map((p) => (p.type === "text" && "content" in p ? String(p.content) : ""))
          .join("");
        if (text.trim()) {
          await workStore.saveMessage(
            newId,
            { role: m.role as "user" | "assistant", content: text },
            { userId: uid, orgId: orgRef.current },
          );
        }
      }

      pendingLoadRef.current = newId;
      setActiveKind("work");
      await refreshSessions();
      toast.success("Promoted to work session", {
        description: "History copied — this session now lives in your workspace.",
      });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Promotion failed");
    }
  }, [activeKind, getToken, refreshSessions, sessions]);

  const answerQuestion = useCallback(
    (q: { runId: string; approved?: boolean; value?: string }) => {
      setPausedRun((prev) => (prev?.runId === q.runId ? null : prev));
    },
    [],
  );

  /**
   * Answer an `awaiting_input` run — POST /resume flips it to
   * specialist_complete, pg_net wakes the brain, and the resumed response
   * arrives via the run watcher (pausedRun → awaiting mode).
   */
  const resumeRun = useCallback(
    async (runId: string, opts: { approved?: boolean; value?: string }) => {
      const approved = opts.approved !== false;
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const token = sessionData.session?.access_token;
        if (!token) throw new Error("Sign in required");

        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
        const res = await fetch(`${supabaseUrl}/functions/v1/brain/resume`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ runId, approved, value: opts.value }),
        });
        if (!res.ok) {
          const body = (await res.json().catch(() => ({}))) as { error?: string };
          throw new Error(body.error ?? `Resume failed (${res.status})`);
        }
        // Watch the resumed run — response_output lands via onComplete.
        setPausedRun({ runId, status: "awaiting" });
        toast.success(approved ? "Answer sent — resuming" : "Rejected");
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Resume failed");
      }
    },
    [],
  );

  return {
    kind,
    store,

    // session state
    sessionKind: activeKind,
    sessionId,
    sessions,
    sessionLists,
    selectSession,
    isLoadingMessages,
    loadSession,
    startNewSession,
    deleteSession,
    renameSession,
    refreshSessions,

    // messages (TanStack UIMessage[])
    messages: tanstackChat.messages,
    isLoading: tanstackChat.isLoading,
    /** Shared-stream generation state (reflects RUN_STARTED..FINISHED). */
    isGenerating: tanstackChat.sessionGenerating ?? tanstackChat.isLoading,
    connectionStatus: tanstackChat.connectionStatus,
    error: tanstackChat.error,
    send,
    stop,
    reload: tanstackChat.reload,
    queuedCount,

    // timeline + runs
    timeline,
    runs,
    pausedRun,
    answerQuestion,
    resumeRun,
    promoteToWorkSession,
    isHydratingTimeline: persisted.isLoading,

    // settings surface for composer/context tab
    selectedModel,
    setSelectedModel,
    selectedProvider,
    setSelectedProvider,
    reasoningEffort,
    setReasoningEffort,
    selectedSpecialist,
    setSelectedSpecialist,
    selectedNotebookId,
    setSelectedNotebookId,

    // composer extras
    pendingAttachments,
    handleFileUploaded,
    isGeminiDelegated: isGeminiOAuthConnected,
  };
}

export type CortexEngine = ReturnType<typeof useCortexEngine>;
